import { useAppSelector } from '@/common/reducers/hooks/store.hook';
import { waWallpaperSx } from '@common/components/wa-wallpaper/wa-wallpaper';
import { Box, Divider, Paper, Typography } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { AiOutlineSync } from 'react-icons/ai';
import InlineContextMessageContainer from './InlineContextMessageContainer';
import PreviewMessageBubble from './preview-message-bubble';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { formatInLanguage } from '@common/config/moment-locales';
import type { UserMessage } from '@bt-common/schemas/bt/user-message';

interface PreviewScheduleMessageProps {
  /** Preview this message instead of the live editor draft — e.g. the template
   *  picker's selected template, which is not loaded into the editor until
   *  "Use template". Every surface shows the same WhatsApp preview this way. */
  messageOverride?: Partial<UserMessage>;
}

const PreviewScheduleMessage: React.FC<PreviewScheduleMessageProps> = ({ messageOverride }) => {
  const { t, i18n } = useTranslation(namespace);
  // Use messageDraft for real-time preview updates (avoids race conditions with wrapper)
  const reduxDraft = useAppSelector(state => state.AddScheduledMessageButtonReducer.messageDraft);
  const messageDraft = messageOverride ?? reduxDraft;

  const renderDateDisplay = () => {
    // BE#657 — don't preview a date the user hasn't explicitly picked (seeded ~now).
    if (!messageDraft.dueDate || !(messageDraft as { dueDateSetByUser?: boolean }).dueDateSetByUser) return null;

    const scheduleDate = moment(messageDraft.dueDate);
    const isRecurring = messageDraft.isRecurring;

    return (
      <InlineContextMessageContainer>
        {isRecurring && <AiOutlineSync />}
        {/* 'LL', not 'MMMM D, YYYY': the explicit pattern localised the month NAME but froze
            the US month-day ORDER, so Hebrew read "ספטמבר 10, 2026" instead of
            "10 בספטמבר 2026" — and the very same dialog spells the date correctly one pane
            over. 'LL' is moment's locale-defined long date, so the order follows the reader. */}
        {formatInLanguage(scheduleDate, i18n.language, 'LL')}
      </InlineContextMessageContainer>
    );
  };


  return (
    <Paper
      elevation={0}
      translate="no"
      sx={{
        width: '100%',
        height: '100%',
        // The wallpaper follows the theme through CSS: it used to key on
        // `whatsappDarkMode`, which the web app never writes, so beside a dark
        // dialog this pane stayed cream in every order of actions.
        ...waWallpaperSx(),
        backgroundSize: 'auto',
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}
    >
      <Box sx={{
        backgroundColor: 'var(--color-surface)',
        textAlign: 'center',
      }}>
        <Typography
          variant="h6"
          gutterBottom
          translate="yes"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mt: '11px',
            color: 'var(--color-text)'
          }}
        >
          {t('messageScheduling.whatsappPreview')}
        </Typography>
        <Divider sx={{ flexShrink: 0 }} />
      </Box>
      <Box sx={{
        p: 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}>
        {/* Main content area */}
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'auto'
        }}>
          {/* WhatsApp chat container */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            width: '100%',
            minWidth: 0,
            alignItems: 'center',
          }}>
            {/* Date display */}
            {renderDateDisplay()}

            {/* Message container */}
            <PreviewMessageBubble messageOverride={messageOverride} />
          </Box>
        </Box>

        {/* Alerts section */}
        <Box sx={{
          mt: 'auto',
          pt: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          flexShrink: 0,
          width: '100%'
        }}>
          {messageDraft?.cancelIfReceived && (
            <Box sx={{
              p: 1.5,
              bgcolor: 'var(--notification-bg)',
              borderRadius: '8px',
              border: '1px solid var(--notification-border)',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'var(--status-warning)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  wordBreak: 'break-word'
                }}
              >
                {t('scheduledMessage.preview.cancelIfReceived')}
              </Typography>
            </Box>
          )}

          {/* The past-time warning used to live HERE, inside the WhatsApp
              preview panel. That panel is `display:none` below the md
              breakpoint, so on a phone the warning did not exist at all — and
              even on desktop it sat in the pane that shows what the message
              LOOKS like, not the pane where the send decision is made. It now
              renders in ScheduleSummary, directly above the Send button, in
              both layouts. `cancelIfReceived` stays here because it is a
              property of the message itself. */}
        </Box>
      </Box>
    </Paper>
  );
};

export default PreviewScheduleMessage; 